// src/pages/admin/BannerManagement.tsx

import { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Eye, EyeOff, Image as ImageIcon, ExternalLink, Layout, BarChart3, Save, X, Search, Filter, RefreshCw, Maximize2, AlignLeft, AlignCenter, AlignRight, Code, Download, Video } from 'lucide-react';
import { AdminLayout } from '../../components/AdminLayout';
import { supabase } from '../../lib/supabase';
import { ImageUpload } from '../../components/ImageUpload';
import { getSessionToken } from '../../contexts/AuthContext';
import jsPDF from 'jspdf';

// --- INTERFACES & CONSTANTS ---

interface Banner {
  id: string;
  title: string;
  image_url?: string; // Made optional since it can be null for video banners
  video_url?: string;
  link_url: string | null;
  page_location: string;
  position_key: string;
  banner_type: 'square' | 'rectangular';
  display_order: number;
  is_active: boolean;
  dimensions: {
    width: string;
    height: string;
  };
  alignment?: 'left' | 'center' | 'right';
  margin_top?: string;
  margin_bottom?: string;
  margin_left?: string;
  margin_right?: string;
  content_type?: 'image' | 'video'; // Simplified content type
  link_type?: 'internal' | 'external' | 'none';
  click_count: number;
  view_count: number;
  created_at: string;
  updated_at: string;
}

const PAGE_LOCATIONS = [
  { value: 'home', label: 'Page d\'accueil', icon: '🏠' },
  { value: 'listings', label: 'Annonces', icon: '🚗' },
  { value: 'actualite', label: 'Actualités', icon: '📰' },
  { value: 'estimateur', label: 'Estimateur', icon: '🔍' },
  { value: 'car-prices', label: 'Prix de Neuf', icon: '💰' }
];

const BANNER_POSITIONS = [
  { value: 'top-banner', label: '🔝 Bannière supérieure', type: 'rectangular', description: 'Bannière horizontale en haut de page' },
  { value: 'bottom-banner', label: '🔽 Bannière inférieure', type: 'rectangular', description: 'Bannière horizontale en bas de page' },
  { value: 'home-hero', label: '🏠 Sous le hero (accueil)', type: 'rectangular', description: 'Bannière sous la section hero sur la page d\'accueil' },
  { value: 'home-featured-listings', label: '🚗 Sous "Prix de Neuf"', type: 'rectangular', description: 'Bannière sous la section "Prix de Neuf" sur la page d\'accueil' },
  { value: 'home-latest-news', label: '📰 Sous "Annonces Récentes"', type: 'rectangular', description: 'Bannière sous la section "Annonces Récentes" sur la page d\'accueil' },
  { value: 'listings-hero', label: '🚗 Sous le hero (annonces)', type: 'rectangular', description: 'Bannière sous la section hero sur la page des annonces' },
  { value: 'actualite-hero', label: '📰 Sous le hero (actualités)', type: 'rectangular', description: 'Bannière sous la section hero sur la page des actualités' },
  { value: 'estimateur-hero', label: '🔍 Sous le hero (estimateur)', type: 'rectangular', description: 'Bannière sous la section hero sur la page estimateur' },
  { value: 'car-prices-hero', label: '💰 Sous le hero (prix de neuf)', type: 'rectangular', description: 'Bannière sous la section hero sur la page des prix de neuf' }
];

const PRESET_SIZES = {
  'square': [
    { label: 'Petit (250x250)', width: '250px', height: '250px' },
    { label: 'Moyen (300x300)', width: '300px', height: '300px' },
    { label: 'Grand (400x400)', width: '400px', height: '400px' },
    { label: 'Auto', width: 'auto', height: 'auto' }
  ],
  'rectangular': [
    { label: 'Petit (728x90)', width: '728px', height: '90px' },
    { label: 'Moyen (870x235)', width: '870px', height: '235px' },
    { label: 'Grand (1200x300)', width: '1200px', height: '300px' },
    { label: 'Plein largeur', width: '100%', height: '250px' },
    { label: 'Auto', width: 'auto', height: 'auto' }
  ]
};

const PRESET_MARGINS = [
  { label: 'Aucun', value: '0px' },
  { label: 'Petit', value: '8px' },
  { label: 'Moyen', value: '16px' },
  { label: 'Grand', value: '24px' },
  { label: 'Très grand', value: '32px' }
];

// --- HELPER COMPONENTS ---

// Video upload component
const VideoUpload = ({ onVideoUploaded, currentVideo }: { onVideoUploaded: (url: string) => void, currentVideo?: string }) => {
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(currentVideo || '');

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // --- START: CORRECTED AUTHENTICATION CHECK ---
    console.log("Attempting to get user session...");

    // First, check for an admin session token
    const adminToken = getSessionToken();
    if (adminToken) {
      console.log("Admin session token found. Proceeding with upload.");
    } else {
      // If no admin token, check for a regular Supabase session
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError) {
        console.error("Session error:", sessionError);
        alert("Authentication error: " + sessionError.message);
        return;
      }

      if (!session) {
        console.error("No active session found. User is not logged in.");
        alert("You must be logged in to upload a video. Please log in again.");
        return;
      }

      console.log("Supabase session found, user is:", session.user);
    }
    // --- END: AUTHENTICATION CHECK ---

    if (!file.type.startsWith('video/')) {
      alert('Please select a video file');
      return;
    }

    setUploading(true);
    try {
      const fileName = `banner-video-${Date.now()}.${file.name.split('.').pop()}`;
      
      const { data, error } = await supabase.storage
        .from('banner-videos')
        .upload(fileName, file);

      if (error) throw error;

      const { data: { publicUrl } } = supabase.storage
        .from('banner-videos')
        .getPublicUrl(fileName);

      onVideoUploaded(publicUrl);
      setPreview(publicUrl);
    } catch (error) {
      console.error('Error uploading video:', error);
      alert('Error uploading video');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center space-x-3">
        <label className="cursor-pointer bg-white border border-gray-300 rounded-lg px-4 py-2 hover:bg-gray-50 transition-colors flex items-center space-x-2">
          <Video className="h-4 w-4" />
          <span>{uploading ? 'Téléchargement...' : 'Choisir une vidéo'}</span>
          <input  
            type="file"
            accept="video/*"
            onChange={handleFileChange}
            className="hidden"
            disabled={uploading}
          />
        </label>
      </div>
      {preview && (
        <div className="mt-3">
          <p className="text-sm text-gray-600 mb-2">Video Preview:</p>
          <video
            src={preview}
            controls
            className="w-full max-w-md rounded-lg border border-gray-200"
          />
        </div>
      )}
    </div>
  );
};


// --- MAIN COMPONENT ---

export function BannerManagement() {
  const [banners, setBanners] = useState<Banner[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingBanner, setEditingBanner] = useState<Banner | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterPage, setFilterPage] = useState('all');
  const [filterPosition, setFilterPosition] = useState('all');
  const [filterActive, setFilterActive] = useState('all');
  const [submitting, setSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    title: '',
    image_url: '',
    video_url: '',
    link_url: '',
    page_location: 'home',
    position_key: 'top-banner',
    banner_type: 'rectangular' as 'square' | 'rectangular',
    display_order: 0,
    is_active: true,
    dimensions: {
      width: '100%',
      height: '250px'
    },
    alignment: 'center' as 'left' | 'center' | 'right',
    margin_top: '16px',
    margin_bottom: '16px',
    margin_left: '0px',
    margin_right: '0px',
    content_type: 'image' as 'image' | 'video', // Simplified content type
    link_type: 'none' as 'internal' | 'external' | 'none'
  });

  useEffect(() => {
    loadBanners();
  }, []);

  const loadBanners = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('banners')
        .select('*')
        .order('page_location', { ascending: true })
        .order('position_key', { ascending: true })
        .order('display_order', { ascending: true });

      if (error) throw error;
      
      const processedData = (data || []).map(banner => ({
        ...banner,
        link_type: banner.link_type || 'none',
        content_type: banner.content_type || 'image',
        alignment: banner.alignment || 'center',
        margin_top: banner.margin_top || '16px',
        margin_bottom: banner.margin_bottom || '16px',
        margin_left: banner.margin_left || '0px',
        margin_right: banner.margin_right || '0px',
        dimensions: banner.dimensions || { width: 'auto', height: 'auto' },
        video_url: banner.video_url || ''
      }));
      
      setBanners(processedData);
    } catch (error) {
      console.error('Error loading banners:', error);
      alert('Erreur lors du chargement des bannières');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Simplified validation
    if (formData.content_type === 'image' && !formData.image_url) {
      alert('Veuillez télécharger une image');
      return;
    }
    
    if (formData.content_type === 'video' && !formData.video_url) {
      alert('Veuillez télécharger une vidéo');
      return;
    }

    try {
      setSubmitting(true);

      let displayOrder = formData.display_order;
      
      if (!editingBanner) {
        const { data: existingBanners, error: fetchError } = await supabase
          .from('banners')
          .select('display_order')
          .eq('page_location', formData.page_location)
          .eq('position_key', formData.position_key);

        if (fetchError) throw fetchError;
        
        if (existingBanners && existingBanners.length > 0) {
          const maxOrder = Math.max(...existingBanners.map(b => b.display_order));
          displayOrder = maxOrder + 1;
        } else {
          displayOrder = 0;
        }
      }

      // Updated bannerData to properly handle image_url and video_url
      const bannerData: any = {
        title: formData.title,
        image_url: formData.content_type === 'image' ? formData.image_url : null, // Only set image_url for image banners
        video_url: formData.content_type === 'video' ? formData.video_url : null, // Only set video_url for video banners
        html_content: null, // Always null now
        link_url: formData.link_url || null,
        link_type: formData.link_type,
        page_location: formData.page_location,
        position_key: formData.position_key,
        banner_type: formData.banner_type,
        display_order: displayOrder,
        is_active: formData.is_active,
        dimensions: formData.dimensions,
        content_type: formData.content_type,
        alignment: formData.alignment,
        margin_top: formData.margin_top,
        margin_bottom: formData.margin_bottom,
        margin_left: formData.margin_left,
        margin_right: formData.margin_right,
        updated_at: new Date().toISOString()
      };

      if (editingBanner) {
        const { error } = await supabase
          .from('banners')
          .update(bannerData)
          .eq('id', editingBanner.id);

        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('banners')
          .insert([bannerData]);

        if (error) {
          console.error('Insert error:', error);
          if (error.message.includes('unique constraint')) {
            const { data: existingBanners, error: fetchError } = await supabase
              .from('banners')
              .select('display_order')
              .eq('page_location', formData.page_location)
              .eq('position_key', formData.position_key);

            if (!fetchError && existingBanners) {
              const usedOrders = existingBanners.map(b => b.display_order);
              let nextOrder = 0;
              
              while (usedOrders.includes(nextOrder)) {
                nextOrder++;
              }
              
              bannerData.display_order = nextOrder;
              const { error: retryError } = await supabase
                .from('banners')
                .insert([bannerData]);
                
              if (retryError) {
                alert('Erreur: Impossible de créer une bannière pour cette position. Veuillez contacter l\'administrateur.');
                return;
              }
            } else {
              alert('Erreur: Le schéma de la base de données ne permet pas plusieurs bannières à la même position. Veuillez contacter l\'administrateur pour modifier la contrainte unique_position.');
              return;
            }
          } else {
            throw error;
          }
        }
      }

      await loadBanners();
      closeModal();
      alert(editingBanner ? 'Bannière mise à jour avec succès' : 'Bannière créée avec succès');
    } catch (error) {
      console.error('Error saving banner:', error);
      alert('Erreur lors de l\'enregistrement de la bannière: ' + (error as Error).message);
    } finally {
      setSubmitting(false);
    }
  };

const handleDelete = async (id: string) => {
  if (!confirm('Êtes-vous sûr de vouloir supprimer cette bannière ?')) {
    return;
  }

  try {
    // Add count: 'exact' to force Supabase to tell you how many rows were affected
    const { error, count } = await supabase
      .from('banners')
      .delete({ count: 'exact' }) 
      .eq('id', id);

    if (error) throw error;
    
    // The Silent Failure Catcher!
    if (count === 0) {
      throw new Error("Supabase a bloqué la suppression (Vérifiez vos politiques RLS).");
    }

    await loadBanners();
    alert('Bannière supprimée avec succès');
  } catch (error) {
    console.error('Error deleting banner:', error);
    alert('Erreur lors de la suppression de la bannière : ' + (error as Error).message);
  }
};

  const toggleActive = async (banner: Banner) => {
    try {
      const { error } = await supabase
        .from('banners')
        .update({ is_active: !banner.is_active, updated_at: new Date().toISOString() })
        .eq('id', banner.id);

      if (error) throw error;
      await loadBanners();
    } catch (error) {
      console.error('Error toggling banner status:', error);
      alert('Erreur lors de la mise à jour du statut');
    }
  };

  const previewRotation = async (pageLocation: string, positionKey: string) => {
    try {
      const { data, error } = await supabase
        .from('banners')
        .select('*')
        .eq('page_location', pageLocation)
        .eq('position_key', positionKey)
        .eq('is_active', true)
        .order('display_order', { ascending: true });

      if (error) throw error;
      
      if (data && data.length > 0) {
        alert(`Rotation pour ${pageLocation}-${positionKey}:\n\n${data.map((b, i) => `${i+1}. ${b.title}`).join('\n')}\n\nUne de ces bannières sera affichée aléatoirement à chaque visite.`);
      } else {
        alert('Aucune bannière active pour cette position.');
      }
    } catch (error) {
      console.error('Error previewing rotation:', error);
      alert('Erreur lors de la prévisualisation de la rotation.');
    }
  };

  const openModal = (banner?: Banner) => {
    if (banner) {
      setEditingBanner(banner);
      setFormData({
        title: banner.title,
        image_url: banner.image_url || '',
        video_url: banner.video_url || '',
        link_url: banner.link_url || '',
        page_location: banner.page_location,
        position_key: banner.position_key,
        banner_type: banner.banner_type,
        display_order: banner.display_order,
        is_active: banner.is_active,
        dimensions: banner.dimensions || { width: 'auto', height: 'auto' },
        alignment: banner.alignment || 'center',
        margin_top: banner.margin_top || '16px',
        margin_bottom: banner.margin_bottom || '16px',
        margin_left: banner.margin_left || '0px',
        margin_right: banner.margin_right || '0px',
        content_type: banner.content_type || 'image',
        link_type: banner.link_type || 'none'
      });
    } else {
      setEditingBanner(null);
      setFormData({
        title: '',
        image_url: '',
        video_url: '',
        link_url: '',
        page_location: 'home',
        position_key: 'top-banner',
        banner_type: 'rectangular',
        display_order: 0,
        is_active: true,
        dimensions: { width: '100%', height: '250px' },
        alignment: 'center',
        margin_top: '16px',
        margin_bottom: '16px',
        margin_left: '0px',
        margin_right: '0px',
        content_type: 'image',
        link_type: 'none'
      });
    }
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingBanner(null);
  };

  const handleImageUpload = (url: string) => {
    setFormData(prev => ({ ...prev, image_url: url }));
  };

  const handleVideoUpload = (url: string) => {
    setFormData(prev => ({ ...prev, video_url: url }));
  };
  
  const handlePositionChange = (position_key: string) => {
    const positionData = BANNER_POSITIONS.find(p => p.value === position_key);
    const newBannerType = (positionData?.type as 'square' | 'rectangular') || 'rectangular';
    
    let defaultDimensions = { width: 'auto', height: 'auto' };
    if (newBannerType === 'rectangular') {
      defaultDimensions = { width: '100%', height: '250px' };
    } else {
      defaultDimensions = { width: '300px', height: '300px' };
    }
    
    setFormData(prev => ({
      ...prev,
      position_key,
      banner_type: newBannerType,
      dimensions: defaultDimensions
    }));
  };

  const handleDimensionChange = (dimension: 'width' | 'height', value: string) => {
    setFormData(prev => ({
      ...prev,
      dimensions: {
        ...prev.dimensions,
        [dimension]: value
      }
    }));
  };

  const handlePresetSize = (preset: { width: string; height: string }) => {
    setFormData(prev => ({
      ...prev,
      dimensions: {
        width: preset.width,
        height: preset.height
      }
    }));
  };

  const handleMarginChange = (margin: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [margin]: value
    }));
  };

  const generateBannerPDF = async (banner: Banner) => {
    try {
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      
      pdf.setFillColor(212, 69, 44);
      pdf.rect(0, 0, pageWidth, 20, 'F');
      pdf.setTextColor(255, 255, 255);
      pdf.setFontSize(16);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Rapport de Bannière', pageWidth / 2, 13, { align: 'center' });
      
      pdf.setTextColor(100, 100, 100);
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'normal');
      const currentDate = new Date().toLocaleDateString('fr-FR');
      const currentTime = new Date().toLocaleTimeString('fr-FR');
      pdf.text(`Généré le: ${currentDate} à ${currentTime}`, 15, 30);
      
      pdf.setTextColor(50, 50, 50);
      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'bold');
      pdf.text(`Titre: ${banner.title}`, 15, 40);
      
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'normal');
      let contentTypeLabel = 'Image';
      if (banner.content_type === 'video') contentTypeLabel = 'Vidéo';
      
      pdf.text(`Type de contenu: ${contentTypeLabel}`, 15, 50);
      
      var currentY = 60;
      if (banner.content_type === 'image' && banner.image_url) {
        try {
          const img = new Image();
          img.crossOrigin = 'anonymous';
          
          await new Promise((resolve, reject) => {
            img.onload = resolve;
            img.onerror = reject;
            img.src = banner.image_url;
          });
          
          const imgWidth = pageWidth - 30;
          const imgHeight = (img.height * imgWidth) / img.width;
          
          pdf.addImage(img, 'JPEG', 15, currentY, imgWidth, Math.min(imgHeight, 80));
          
          currentY = currentY + Math.min(imgHeight, 80) + 10;
        } catch (error) {
          console.error('Error loading banner image:', error);
          pdf.text('Image non disponible', 15, currentY);
          currentY = currentY + 10;
        }
      } else if (banner.content_type === 'video' && banner.video_url) {
        pdf.text('Vidéo incluse dans la bannière', 15, currentY);
        currentY = currentY + 10;
      } else {
        pdf.text('Média non disponible', 15, currentY);
        currentY = currentY + 10;
      }
      
      pdf.setFontSize(12);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Statistiques', 15, currentY);
      currentY = currentY + 8;
      
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'normal');
      pdf.text(`Nombre de vues: ${banner.view_count || 0}`, 15, currentY);
      currentY = currentY + 5;
      pdf.text(`Nombre de clics: ${banner.click_count || 0}`, 15, currentY);
      currentY = currentY + 5;
      
      if (banner.view_count && banner.view_count > 0) {
        const clickRate = ((banner.click_count || 0) / banner.view_count * 100).toFixed(2);
        pdf.text(`Taux de clics: ${clickRate}%`, 15, currentY);
        currentY = currentY + 5;
      }
      
      currentY = currentY + 5;
      pdf.setFontSize(12);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Détails de la bannière', 15, currentY);
      currentY = currentY + 8;
      
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'normal');
      pdf.text(`Type de bannière: ${banner.banner_type === 'square' ? 'Carrée' : 'Rectangulaire'}`, 15, currentY);
      currentY = currentY + 5;
      pdf.text(`Alignement: ${banner.alignment === 'left' ? 'Gauche' : banner.alignment === 'right' ? 'Droite' : 'Centre'}`, 15, currentY);
      currentY = currentY + 5;
      pdf.text(`Statut: ${banner.is_active ? 'Active' : 'Inactive'}`, 15, currentY);
      currentY = currentY + 5;
      
      if (banner.link_url) {
        pdf.text(`Type de lien: ${banner.link_type === 'internal' ? 'Interne' : banner.link_type === 'external' ? 'Externe' : 'Aucun'}`, 15, currentY);
        currentY = currentY + 5;
        pdf.text(`URL: ${banner.link_url}`, 15, currentY);
        currentY = currentY + 5;
      }
      
      currentY = currentY + 5;
      pdf.setFontSize(12);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Informations de date', 15, currentY);
      currentY = currentY + 8;
      
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'normal');
      const creationDate = new Date(banner.created_at).toLocaleDateString('fr-FR');
      pdf.text(`Date de création: ${creationDate}`, 15, currentY);
      currentY = currentY + 5;
      
      const updateDate = new Date(banner.updated_at).toLocaleDateString('fr-FR');
      pdf.text(`Dernière mise à jour: ${updateDate}`, 15, currentY);
      currentY = currentY + 5;
      
      pdf.setFillColor(240, 240, 240);
      pdf.rect(0, pageHeight - 15, pageWidth, 15, 'F');
      pdf.setTextColor(100, 100, 100);
      pdf.setFontSize(8);
      pdf.setFont('helvetica', 'normal');
      pdf.text('Ce rapport a été généré automatiquement par le système de gestion des bannières', pageWidth / 2, pageHeight - 8, { align: 'center' });
      
      pdf.save(`banner_${banner.title.replace(/\s+/g, '_')}_${Date.now()}.pdf`);
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Erreur lors de la génération du PDF');
    }
  };

  const filteredBanners = banners.filter(banner => {
    const matchesSearch = banner.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         banner.page_location.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesPage = filterPage === 'all' || banner.page_location === filterPage;
    const matchesPosition = filterPosition === 'all' || banner.position_key === filterPosition;
    const matchesActive = filterActive === 'all' || 
                         (filterActive === 'active' && banner.is_active) ||
                         (filterActive === 'inactive' && !banner.is_active);
    
    return matchesSearch && matchesPage && matchesPosition && matchesActive;
  });

  const groupedBanners = filteredBanners.reduce((acc, banner) => {
    const key = `${banner.page_location}-${banner.position_key}`;
    if (!acc[key]) {
      acc[key] = [];
    }
    acc[key].push(banner);
    return acc;
  }, {} as Record<string, Banner[]>);

  return (
    <AdminLayout currentPage="banners">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Gestion des Bannières</h1>
            <p className="text-gray-600 text-sm mt-1">
              Système de rotation automatique - Une bannière par position à chaque visite
            </p>
          </div>
          <button
            onClick={() => openModal()}
            className="inline-flex items-center space-x-2 bg-gradient-to-r from-[#D4452C] to-[#b83a24] text-white px-4 py-2 rounded-lg hover:shadow-lg transition-all"
          >
            <Plus className="h-4 w-4" />
            <span>Nouvelle bannière</span>
          </button>
        </div>

        {/* Info Box */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start space-x-3">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="flex-1">
              <h3 className="text-sm font-medium text-blue-900 mb-1">💡 Comment fonctionne le système de rotation ?</h3>
              <ul className="text-sm text-blue-800 space-y-1">
                <li>• Vous pouvez créer <strong>plusieurs bannières</strong> pour la même position</li>
                <li>• À chaque visite de page, <strong>une seule bannière</strong> est affichée aléatoirement</li>
                <li>• Les positions disponibles : Haut, Bas, Gauche, Droite</li>
                <li>• Activez/désactivez les bannières pour contrôler la rotation</li>
                <li>• Vous pouvez maintenant créer des bannières avec <strong>image</strong> ou <strong>vidéo</strong>.</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total bannières</p>
                <p className="text-2xl font-bold text-gray-900">{banners.length}</p>
              </div>
              <Layout className="h-8 w-8 text-blue-500" />
            </div>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Actives</p>
                <p className="text-2xl font-bold text-green-600">{banners.filter(b => b.is_active).length}</p>
              </div>
              <Eye className="h-8 w-8 text-green-500" />
            </div>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Inactives</p>
                <p className="text-2xl font-bold text-gray-600">{banners.filter(b => !b.is_active).length}</p>
              </div>
              <EyeOff className="h-8 w-8 text-gray-400" />
            </div>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Vues totales</p>
                <p className="text-2xl font-bold text-purple-600">
                  {banners.reduce((sum, b) => sum + (b.view_count || 0), 0)}
                </p>
              </div>
              <BarChart3 className="h-8 w-8 text-purple-500" />
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Rechercher..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D4452C] text-sm"
              />
            </div>
            <select
              value={filterPage}
              onChange={(e) => setFilterPage(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D4452C] text-sm"
            >
              <option value="all">Toutes les pages</option>
              {PAGE_LOCATIONS.map(page => (
                <option key={page.value} value={page.value}>{page.icon} {page.label}</option>
              ))}
            </select>
            <select
              value={filterPosition}
              onChange={(e) => setFilterPosition(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D4452C] text-sm"
            >
              <option value="all">Toutes les positions</option>
              {BANNER_POSITIONS.map(pos => (
                <option key={pos.value} value={pos.value}>{pos.label}</option>
              ))}
            </select>
            <select
              value={filterActive}
              onChange={(e) => setFilterActive(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D4452C] text-sm"
            >
              <option value="all">Tous les statuts</option>
              <option value="active">Actives uniquement</option>
              <option value="inactive">Inactives uniquement</option>
            </select>
          </div>
          <div className="mt-3 flex items-center justify-between">
            <p className="text-sm text-gray-600">
              {filteredBanners.length} bannière{filteredBanners.length > 1 ? 's' : ''} trouvée{filteredBanners.length > 1 ? 's' : ''}
            </p>
            <button
              onClick={loadBanners}
              className="text-sm text-[#D4452C] hover:text-[#b83a24] flex items-center space-x-1"
            >
              <RefreshCw className="h-3 w-3" />
              <span>Actualiser</span>
            </button>
          </div>
        </div>

        {/* Banners List */}
        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#D4452C] mx-auto mb-4"></div>
              <p className="text-gray-600">Chargement des bannières...</p>
            </div>
          </div>
        ) : Object.keys(groupedBanners).length === 0 ? (
          <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
            <ImageIcon className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Aucune bannière</h3>
            <p className="text-gray-600 mb-6">
              Commencez par créer votre première bannière
            </p>
            <button
              onClick={() => openModal()}
              className="inline-flex items-center space-x-2 bg-[#D4452C] text-white px-4 py-2 rounded-lg hover:bg-[#b83a24] transition-colors"
            >
              <Plus className="h-4 w-4" />
              <span>Créer une bannière</span>
            </button>
          </div>
        ) : (
          <div className="space-y-6">
            {Object.entries(groupedBanners).map(([groupKey, groupBanners]) => {
              const [pageLocation, positionKey] = groupKey.split('-');
              const pageInfo = PAGE_LOCATIONS.find(p => p.value === pageLocation);
              
              const correctPositionInfo = BANNER_POSITIONS.find(p => {
                const posFromKey = groupKey.replace(`${pageLocation}-`, '');
                return p.value === posFromKey;
              });
              
              return (
                <div key={groupKey} className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                  <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
                    <div className="flex items-center justify-between">
                      <h3 className="font-semibold text-gray-900 flex items-center">
                        <span className="mr-2">{pageInfo?.icon}</span>
                        {pageInfo?.label} 
                        <span className="mx-2 text-gray-400">→</span>
                        <span className="text-sm text-gray-600">{correctPositionInfo?.label}</span>
                        <span className="ml-2 text-sm text-gray-500">({groupBanners.length} bannière{groupBanners.length > 1 ? 's' : ''} en rotation)</span>
                      </h3>
                      <div className="flex items-center space-x-2">
                        {groupBanners.length > 1 && (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            🔄 Rotation active
                          </span>
                        )}
                        <button
                          onClick={() => previewRotation(pageLocation, positionKey)}
                          className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800 hover:bg-gray-200"
                        >
                          👁️ Aperçu
                        </button>
                      </div>
                    </div>
                    {correctPositionInfo && (
                      <p className="text-xs text-gray-500 mt-1">{correctPositionInfo.description}</p>
                    )}
                  </div>
                  <div className="divide-y divide-gray-200">
                    {groupBanners.map((banner) => (
                      <div key={banner.id} className="p-4 hover:bg-gray-50 transition-colors">
                        <div className="flex items-center space-x-4">
                          <div className={`flex-shrink-0 rounded-lg overflow-hidden border border-gray-200 ${
                            banner.banner_type === 'square' ? 'w-20 h-20' : 'w-32 h-20'
                          }`}>
                            {banner.content_type === 'image' ? (
                              <img
                                src={banner.image_url}
                                alt={banner.title}
                                className="w-full h-full object-cover"
                                onError={(e) => {
                                  (e.target as HTMLImageElement).src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgZmlsbD0iI2VlZSIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMTQiIGZpbGw9IiM5OTkiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIj5JbWFnZSBub24gZGlzcG9uaWJsZTwvdGV4dD48L3N2Zz4=';
                                }}
                              />
                            ) : banner.content_type === 'video' ? (
                              <div className="w-full h-full bg-gray-100 flex items-center justify-center">
                                <Video className="h-8 w-8 text-gray-400" />
                              </div>
                            ) : (
                              <div className="w-full h-full bg-gray-100 flex items-center justify-center">
                                <ImageIcon className="h-8 w-8 text-gray-400" />
                              </div>
                            )}
                          </div>

                          <div className="flex-1 min-w-0">
                            <div className="flex items-center space-x-2 mb-1">
                              <h4 className="font-semibold text-gray-900 truncate">{banner.title}</h4>
                              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                                banner.is_active
                                  ? 'bg-green-100 text-green-800'
                                  : 'bg-gray-100 text-gray-800'
                              }`}>
                                {banner.is_active ? 'Active' : 'Inactive'}
                              </span>
                              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                                banner.content_type === 'video'
                                  ? 'bg-red-100 text-red-800'
                                  : 'bg-green-100 text-green-800'
                              }`}>
                                {banner.content_type === 'video' ? 'Vidéo' : 'Image'}
                              </span>
                            </div>
                            <div className="flex items-center text-xs text-gray-500 space-x-3">
                              <span className="flex items-center">
                                <BarChart3 className="h-3 w-3 mr-1" />
                                {banner.view_count || 0} vues • {banner.click_count || 0} clics
                              </span>
                              <span className="flex items-center">
                                <Maximize2 className="h-3 w-3 mr-1" />
                                {banner.dimensions?.width || 'auto'} × {banner.dimensions?.height || 'auto'}
                              </span>
                              {banner.alignment && (
                                <span className="flex items-center">
                                  {banner.alignment === 'left' && <AlignLeft className="h-3 w-3 mr-1" />}
                                  {banner.alignment === 'center' && <AlignCenter className="h-3 w-3 mr-1" />}
                                  {banner.alignment === 'right' && <AlignRight className="h-3 w-3 mr-1" />}
                                </span>
                              )}
                            </div>
                            {banner.link_url && (
                              <div className="mt-1 flex items-center text-xs text-blue-600">
                                <ExternalLink className="h-3 w-3 mr-1" />
                                <span className="truncate">{banner.link_url}</span>
                              </div>
                            )}
                          </div>

                          <div className="flex items-center space-x-2">
                            <button
                              onClick={() => toggleActive(banner)}
                              className={`p-2 rounded-lg transition-colors ${
                                banner.is_active
                                  ? 'text-green-600 hover:bg-green-50'
                                  : 'text-gray-400 hover:bg-gray-100'
                              }`}
                              title={banner.is_active ? 'Désactiver' : 'Activer'}
                            >
                              {banner.is_active ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                            </button>
                            <button
                              onClick={() => generateBannerPDF(banner)}
                              className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                              title="Télécharger en PDF"
                            >
                              <Download className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => openModal(banner)}
                              className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                              title="Modifier"
                            >
                              <Edit2 className="h-4 w-4" />
                            </button>
<button
  type="button"
  onClick={() => handleDelete(banner.id)}
  className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
  title="Supprimer"
>
  <Trash2 className="h-4 w-4" />
</button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 overflow-y-auto">
            <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10">
                <h2 className="text-xl font-bold text-gray-900">
                  {editingBanner ? 'Modifier la bannière' : 'Nouvelle bannière'}
                </h2>
                <button
                  onClick={closeModal}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="p-6 space-y-4">
                {/* Title */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Titre de la bannière *
                  </label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D4452C]"
                    required
                    placeholder="Ex: Promotion été 2024"
                  />
                </div>

                {/* Page Location */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Page de destination *
                  </label>
                  <select
                    value={formData.page_location}
                    onChange={(e) => setFormData(prev => ({ ...prev, page_location: e.target.value }))}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D4452C]"
                    required
                  >
                    {PAGE_LOCATIONS.map(page => (
                      <option key={page.value} value={page.value}>
                        {page.icon} {page.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Position */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Position sur la page *
                  </label>
                  <select
                    value={formData.position_key}
                    onChange={(e) => handlePositionChange(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D4452C]"
                    required
                  >
                    {BANNER_POSITIONS.map(pos => (
                      <option key={pos.value} value={pos.value}>
                        {pos.label}
                      </option>
                    ))}
                  </select>
                  <p className="mt-1 text-xs text-gray-500">
                    {BANNER_POSITIONS.find(p => p.value === formData.position_key)?.description}
                  </p>
                  <p className="mt-1 text-xs text-blue-600">
                    💡 Plusieurs bannières peuvent être créées pour cette position et seront affichées en rotation
                  </p>
                </div>

                {/* Content Type */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Type de contenu *
                  </label>
                  <div className="grid grid-cols-2 gap-4">
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="content_type"
                        value="image"
                        checked={formData.content_type === 'image'}
                        onChange={() => setFormData(prev => ({ ...prev, content_type: 'image' }))}
                        className="mr-2"
                      />
                      <span>Image</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="content_type"
                        value="video"
                        checked={formData.content_type === 'video'}
                        onChange={() => setFormData(prev => ({ ...prev, content_type: 'video' }))}
                        className="mr-2"
                      />
                      <span>Vidéo</span>
                    </label>
                  </div>
                </div>

                {/* Content based on type */}
                {formData.content_type === 'image' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Image de la bannière *
                    </label>
                    <ImageUpload
                      onImageUploaded={handleImageUpload}
                      currentImage={formData.image_url}
                    />
                  </div>
                )}

                {formData.content_type === 'video' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Vidéo de la bannière *
                    </label>
                    <VideoUpload
                      onVideoUploaded={handleVideoUpload}
                      currentVideo={formData.video_url}
                    />
                  </div>
                )}

            {/* Dimensions */}
                <div className="bg-gray-50 p-4 rounded-xl border border-gray-200">
                  <label className="block text-sm font-semibold text-gray-900 mb-3">
                    Dimensions & Formats
                  </label>
                  
                  <div className="mb-4">
                    <p className="text-xs text-gray-500 mb-2">Sélectionnez une taille prédéfinie :</p>
                    <div className="flex flex-wrap gap-2">
                      {(PRESET_SIZES[formData.banner_type] || PRESET_SIZES['rectangular'] || []).map((preset, index) => (
                        <button
                          key={index}
                          type="button"
                          onClick={() => handlePresetSize(preset)}
                          className={`px-3 py-1.5 text-xs rounded-lg border transition-colors ${
                            formData.dimensions.width === preset.width && 
                            formData.dimensions.height === preset.height
                              ? 'bg-[#D4452C] text-white border-[#D4452C] font-medium'
                              : 'bg-white text-gray-700 border-gray-300 hover:border-[#D4452C]'
                          }`}
                        >
                          {preset.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="pt-4 border-t border-gray-200">
                    <p className="text-sm font-medium text-gray-800 mb-1">Ou entrez des dimensions exactes :</p>
                    <p className="text-xs text-blue-600 mb-3">💡 Si vous entrez des pixels exacts (ex: 800 et 250), l'image ne sera jamais coupée. Elle s'adaptera parfaitement aux écrans mobiles en conservant sa forme !</p>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-semibold text-gray-700 mb-1">Largeur exacte (pixels)</label>
                        <div className="relative">
                          <input
                            type="text"
                            value={formData.dimensions.width.replace('px', '')}
                            onChange={(e) => handleDimensionChange('width', e.target.value ? `${e.target.value}px` : 'auto')}
                            className="w-full pl-3 pr-10 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D4452C] text-sm"
                            placeholder="Ex: 800"
                          />
                          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400 font-medium">px</span>
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-700 mb-1">Hauteur exacte (pixels)</label>
                        <div className="relative">
                          <input
                            type="text"
                            value={formData.dimensions.height.replace('px', '')}
                            onChange={(e) => handleDimensionChange('height', e.target.value ? `${e.target.value}px` : 'auto')}
                            className="w-full pl-3 pr-10 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D4452C] text-sm"
                            placeholder="Ex: 250"
                          />
                          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400 font-medium">px</span>
                        </div>
                      </div>
                    </div>
                                        </div>
                                                            </div>

                {/* Alignment */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Alignement
                  </label>
                  <div className="flex space-x-2">
                    <button
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, alignment: 'left' }))}
                      className={`flex items-center justify-center px-4 py-2 rounded-lg border transition-colors ${
                        formData.alignment === 'left'
                          ? 'bg-[#D4452C] text-white border-[#D4452C]'
                          : 'bg-white text-gray-700 border-gray-300 hover:border-[#D4452C]'
                      }`}
                    >
                      <AlignLeft className="h-4 w-4 mr-2" />
                      Gauche
                    </button>
                    <button
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, alignment: 'center' }))}
                      className={`flex items-center justify-center px-4 py-2 rounded-lg border transition-colors ${
                        formData.alignment === 'center'
                          ? 'bg-[#D4452C] text-white border-[#D4452C]'
                          : 'bg-white text-gray-700 border-gray-300 hover:border-[#D4452C]'
                      }`}
                    >
                      <AlignCenter className="h-4 w-4 mr-2" />
                      Centre
                    </button>
                    <button
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, alignment: 'right' }))}
                      className={`flex items-center justify-center px-4 py-2 rounded-lg border transition-colors ${
                        formData.alignment === 'right'
                          ? 'bg-[#D4452C] text-white border-[#D4452C]'
                          : 'bg-white text-gray-700 border-gray-300 hover:border-[#D4452C]'
                      }`}
                    >
                      <AlignRight className="h-4 w-4 mr-2" />
                      Droite
                    </button>
                  </div>
                </div>

                {/* Margins */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Marges
                  </label>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Haut</label>
                      <select
                        value={formData.margin_top}
                        onChange={(e) => handleMarginChange('margin_top', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D4452C] text-sm"
                      >
                        {PRESET_MARGINS.map((margin) => (
                          <option key={margin.value} value={margin.value}>
                            {margin.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Bas</label>
                      <select
                        value={formData.margin_bottom}
                        onChange={(e) => handleMarginChange('margin_bottom', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D4452C] text-sm"
                      >
                        {PRESET_MARGINS.map((margin) => (
                          <option key={margin.value} value={margin.value}>
                            {margin.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Gauche</label>
                      <select
                        value={formData.margin_left}
                        onChange={(e) => handleMarginChange('margin_left', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D4452C] text-sm"
                      >
                        {PRESET_MARGINS.map((margin) => (
                          <option key={margin.value} value={margin.value}>
                            {margin.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Droite</label>
                      <select
                        value={formData.margin_right}
                        onChange={(e) => handleMarginChange('margin_right', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D4452C] text-sm"
                      >
                        {PRESET_MARGINS.map((margin) => (
                          <option key={margin.value} value={margin.value}>
                            {margin.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                {/* Link Type */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Type de lien
                  </label>
                  <div className="flex space-x-4">
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="link_type"
                        value="none"
                        checked={formData.link_type === 'none'}
                        onChange={() => setFormData(prev => ({ ...prev, link_type: 'none', link_url: '' }))}
                        className="mr-2"
                      />
                      <span>Aucun lien</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="link_type"
                        value="internal"
                        checked={formData.link_type === 'internal'}
                        onChange={() => setFormData(prev => ({ ...prev, link_type: 'internal' }))}
                        className="mr-2"
                      />
                      <span>Lien interne</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="link_type"
                        value="external"
                        checked={formData.link_type === 'external'}
                        onChange={() => setFormData(prev => ({ ...prev, link_type: 'external' }))}
                        className="mr-2"
                      />
                      <span>Lien externe</span>
                    </label>
                  </div>
                </div>

                {/* Link URL */}
                {formData.link_type !== 'none' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      URL {formData.link_type === 'internal' ? '(chemin interne)' : '(URL complète)'} *
                    </label>
                    <input
                      type="text"
                      value={formData.link_url}
                      onChange={(e) => setFormData(prev => ({ ...prev, link_url: e.target.value }))}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D4452C]"
                      placeholder={formData.link_type === 'internal' ? '/listings' : 'https://example.com'}
                      required
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      {formData.link_type === 'internal' 
                        ? 'Ex: /listings, /actualite, /estimator'
                        : 'Ex: https://www.example.com'}
                    </p>
                  </div>
                )}

                <input type="hidden" value={formData.display_order} />

                {/* Active Status */}
                <div className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    id="is_active"
                    checked={formData.is_active}
                    onChange={(e) => setFormData(prev => ({ ...prev, is_active: e.target.checked }))}
                    className="w-4 h-4 text-[#D4452C] border-gray-300 rounded focus:ring-[#D4452C]"
                  />
                  <label htmlFor="is_active" className="text-sm font-medium text-gray-700">
                    Activer la bannière immédiatement
                  </label>
                </div>

                {/* Actions */}
                <div className="flex space-x-3 pt-4 border-t border-gray-200">
                  <button
                    type="submit"
                    disabled={submitting}
                    className="flex-1 bg-gradient-to-r from-[#D4452C] to-[#b83a24] text-white py-2 rounded-lg font-medium hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
                  >
                    <Save className="h-4 w-4" />
                    <span>{submitting ? 'Enregistrement...' : (editingBanner ? 'Mettre à jour' : 'Créer la bannière')}</span>
                  </button>
                  <button
                    type="button"
                    onClick={closeModal}
                    className="px-6 py-2 border border-gray-300 rounded-lg font-medium hover:bg-gray-50 transition-colors"
                  >
                    Annuler
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
